import React, { useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Paperclip, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

function FileField({ formId, value = [], onChange, testid }) {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    setBusy(true);
    const added = [];
    for (const f of files) {
      if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name} melebihi 10 MB`); continue; }
      const fd = new FormData(); fd.append("file", f);
      try { const r = await api.post(`/forms/fill/${formId}/upload`, fd); added.push({ id: r.data.id, name: r.data.name }); }
      catch (err) { toast.error(err.response?.data?.detail || `Gagal mengunggah ${f.name}`); }
    }
    onChange([...value, ...added]);
    setBusy(false);
  };
  return (
    <div className="space-y-2">
      {value.map((f) => (
        <div key={f.id} className="flex items-center gap-2 text-sm rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
          <Paperclip className="h-4 w-4 text-indigo-500" /><span className="flex-1 truncate">{f.name}</span>
          <button type="button" onClick={() => onChange(value.filter((x) => x.id !== f.id))} className="text-slate-400 hover:text-rose-500"><X className="h-4 w-4" /></button>
        </div>
      ))}
      <label className="inline-flex items-center gap-2 cursor-pointer rounded-lg border border-dashed border-indigo-300 px-4 py-2 text-sm font-semibold text-indigo-600 hover:bg-indigo-50 transition-colors">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}{busy ? "Mengunggah..." : "Pilih foto / dokumen"}
        <input type="file" multiple className="hidden" disabled={busy} onChange={upload} data-testid={testid}
          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt" />
      </label>
    </div>
  );
}

export function QuestionField({ q, idx, value, onChange, formId }) {
  const tid = `fill-q-${idx}`;
  switch (q.type) {
    case "paragraph": return <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={4} data-testid={tid} placeholder="Jawaban Anda" />;
    case "number": return <Input type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={tid} placeholder="0" className="max-w-xs" />;
    case "date": return <Input type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={tid} className="max-w-xs" />;
    case "multiple_choice": return (
      <div className="space-y-2" data-testid={tid}>
        {q.options.map((o, i) => (
          <label key={i} className="flex items-center gap-3 cursor-pointer text-slate-700">
            <input type="radio" name={q.id} checked={value === o} onChange={() => onChange(o)} className="h-4 w-4 accent-indigo-600" data-testid={`${tid}-opt-${i}`} />{o}
          </label>
        ))}
      </div>
    );
    case "checkbox": {
      const arr = Array.isArray(value) ? value : [];
      return (
        <div className="space-y-2" data-testid={tid}>
          {q.options.map((o, i) => (
            <label key={i} className="flex items-center gap-3 cursor-pointer text-slate-700">
              <Checkbox checked={arr.includes(o)} onCheckedChange={(c) => onChange(c ? [...arr, o] : arr.filter((x) => x !== o))} data-testid={`${tid}-opt-${i}`} />{o}
            </label>
          ))}
        </div>
      );
    }
    case "dropdown": return (
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger className="max-w-sm" data-testid={tid}><SelectValue placeholder="Pilih" /></SelectTrigger>
        <SelectContent>{q.options.map((o, i) => <SelectItem key={i} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select>
    );
    case "scale": return (
      <div className="flex items-center gap-2 flex-wrap" data-testid={tid}>
        <span className="text-xs text-slate-400 mr-1">Rendah</span>
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" onClick={() => onChange(n)} data-testid={`${tid}-opt-${n}`}
            className={`h-10 w-10 rounded-full border-2 font-bold transition-colors ${value === n ? "bg-indigo-600 border-indigo-600 text-white" : "border-slate-200 text-slate-600 hover:border-indigo-400"}`}>{n}</button>
        ))}
        <span className="text-xs text-slate-400 ml-1">Tinggi</span>
      </div>
    );
    case "file": return <FileField formId={formId} value={value || []} onChange={onChange} testid={tid} />;
    default: return <Input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={tid} placeholder="Jawaban Anda" />;
  }
}
